import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAvailableSlotsThunk,
  getAvailableSlotsRangeThunk,
} from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Input from "../../../../components/common/Input";
import {
  CalendarIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ViewGridIcon,
  ViewListIcon,
  RefreshIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const SlotViewer = ({ admin, availability }) => {
  const dispatch = useDispatch();
  const { availableSlots, availableSlotsRange, loadingSlots } = useSelector(
    (state) => state.admin
  );

  // Local state
  const [viewMode, setViewMode] = useState("single"); // 'single' or 'range'
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split("T")[0];
  });
  const [duration, setDuration] = useState(30);
  const [displayMode, setDisplayMode] = useState("grid"); // 'grid' or 'timeline'

  // Load slots on mount and when date/duration changes
  useEffect(() => {
    if (viewMode === "single" && selectedDate) {
      loadSingleDaySlots();
    }
  }, [selectedDate, duration, viewMode]);

  useEffect(() => {
    if (viewMode === "range" && startDate && endDate) {
      loadRangeSlots();
    }
  }, [startDate, endDate, duration, viewMode]);

  // Load single day slots
  const loadSingleDaySlots = () => {
    dispatch(
      getAvailableSlotsThunk({
        adminCode: admin?.adminCode,
        date: selectedDate,
        duration: duration,
      })
    );
  };

  // Load range slots
  const loadRangeSlots = () => {
    dispatch(
      getAvailableSlotsRangeThunk({
        adminCode: admin?.adminCode,
        startDate: startDate,
        endDate: endDate,
        duration: duration,
      })
    );
  };

  // Navigate to previous day
  const goToPreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  // Navigate to next day
  const goToNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  // Go to today
  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  // Format date display
  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Format time
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get time slot color based on time of day
  const getSlotColor = (timeString) => {
    const [hours] = timeString.split(":");
    const hour = parseInt(hours);

    if (hour >= 6 && hour < 12) {
      return "bg-blue-50 border-blue-200 text-blue-700";
    } else if (hour >= 12 && hour < 17) {
      return "bg-green-50 border-green-200 text-green-700";
    } else if (hour >= 17 && hour < 21) {
      return "bg-orange-50 border-orange-200 text-orange-700";
    } else {
      return "bg-purple-50 border-purple-200 text-purple-700";
    }
  };

  // Group slots by hour for timeline view
  const groupSlotsByHour = (slots) => {
    const grouped = {};
    (slots || []).forEach((slot) => {
      const hour = slot.start.split(":")[0];
      if (!grouped[hour]) {
        grouped[hour] = [];
      }
      grouped[hour].push(slot);
    });
    return grouped;
  };

  // Render slot card
  const renderSlotCard = (slot, index) => {
    return (
      <div
        key={index}
        className={`p-4 rounded-lg border-2 ${getSlotColor(
          slot.start
        )} transition-all hover:shadow-md cursor-pointer`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5" />
            <p className="font-semibold">{formatTime(slot.start)}</p>
          </div>
          <Badge variant="default" className="text-xs">
            {duration} min
          </Badge>
        </div>
        <p className="text-sm opacity-75">Until {formatTime(slot.end)}</p>
        {slot.dateTime && (
          <p className="text-xs mt-2 opacity-60">
            {new Date(slot.dateTime).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </p>
        )}
      </div>
    );
  };

  // Render timeline view
  const renderTimelineView = () => {
    const groupedSlots = groupSlotsByHour(availableSlots);
    const hours = Object.keys(groupedSlots).sort();

    return (
      <div className="space-y-4">
        {hours.map((hour) => (
          <div key={hour} className="flex gap-4">
            <div className="w-20 flex-shrink-0 text-right">
              <p className="font-semibold text-gray-700">
                {formatTime(`${hour}:00`)}
              </p>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {groupedSlots[hour].map((slot, idx) => (
                <div
                  key={idx}
                  className={`p-2 rounded-lg border ${getSlotColor(
                    slot.start
                  )} text-sm`}
                >
                  <p className="font-medium">
                    {formatTime(slot.start)} - {formatTime(slot.end)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render range view
  const renderRangeView = () => {
    const dates = Object.keys(availableSlotsRange || {}).sort();

    return (
      <div className="space-y-6">
        {dates.map((date) => {
          const slots = availableSlotsRange[date];
          return (
            <div key={date} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-gray-900">
                    {formatDateDisplay(date)}
                  </h3>
                </div>
                <Badge variant="info">{slots.length} slots</Badge>
              </div>

              {slots.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {slots.map((slot, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded border text-center text-sm ${getSlotColor(
                        slot.start
                      )}`}
                    >
                      {formatTime(slot.start)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600 text-center py-4">
                  No available slots
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Calculate stats
  const stats = {
    totalSlots:
      viewMode === "single"
        ? (availableSlots || []).length
        : Object.values(availableSlotsRange || {}).reduce(
            (sum, slots) => sum + slots.length,
            0
          ),
    earliestSlot:
      viewMode === "single" && availableSlots?.length > 0
        ? availableSlots[0].start
        : null,
    latestSlot:
      viewMode === "single" && availableSlots?.length > 0
        ? availableSlots[availableSlots.length - 1].start
        : null,
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* View Mode Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            View Mode
          </label>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "single" ? "primary" : "secondary"}
              onClick={() => setViewMode("single")}
              className="flex-1"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Single Day
            </Button>
            <Button
              variant={viewMode === "range" ? "primary" : "secondary"}
              onClick={() => setViewMode("range")}
              className="flex-1"
            >
              <ViewListIcon className="w-4 h-4 mr-2" />
              Date Range
            </Button>
          </div>
        </div>

        {/* Duration Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Appointment Duration
          </label>
          <div className="flex gap-2">
            {[15, 30, 45, 60].map((dur) => (
              <Button
                key={dur}
                variant={duration === dur ? "primary" : "secondary"}
                onClick={() => setDuration(dur)}
                size="sm"
                className="flex-1"
              >
                {dur}m
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Date Selection */}
      {viewMode === "single" ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Date
          </label>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={goToPreviousDay}>
              <ChevronLeftIcon className="w-4 h-4" />
            </Button>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="flex-1"
            />
            <Button variant="secondary" size="sm" onClick={goToNextDay}>
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
            <Button variant="secondary" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadSingleDaySlots}
              disabled={loadingSlots}
            >
              <RefreshIcon
                className={`w-4 h-4 ${loadingSlots ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
          />
          <Input
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
          />
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <ViewGridIcon className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Total Slots</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalSlots}
              </p>
            </div>
          </div>
        </div>

        {viewMode === "single" && stats.earliestSlot && (
          <>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-3">
                <ClockIcon className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Earliest</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatTime(stats.earliestSlot)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-3">
                <ClockIcon className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Latest</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatTime(stats.latestSlot)}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {viewMode === "range" && (
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">Days</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.keys(availableSlotsRange || {}).length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Display Mode Toggle (Single Day Only) */}
      {viewMode === "single" && (
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-900">
            Available Slots for {formatDateDisplay(selectedDate)}
          </h3>
          <div className="flex gap-2">
            <Button
              variant={displayMode === "grid" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setDisplayMode("grid")}
            >
              <ViewGridIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={displayMode === "timeline" ? "primary" : "secondary"}
              size="sm"
              onClick={() => setDisplayMode("timeline")}
            >
              <ViewListIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingSlots && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading available slots...</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!loadingSlots && (
        <>
          {viewMode === "single" ? (
            <>
              {(availableSlots || []).length > 0 ? (
                displayMode === "grid" ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {availableSlots.map((slot, index) =>
                      renderSlotCard(slot, index)
                    )}
                  </div>
                ) : (
                  renderTimelineView()
                )
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    No available slots for this date
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try selecting a different date or duration
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {Object.keys(availableSlotsRange || {}).length > 0 ? (
                renderRangeView()
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    No available slots in this date range
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try adjusting your date range or duration
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Available Slots</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Slots are generated based on your working hours</li>
              <li>Time off and special hours are automatically considered</li>
              <li>
                Slot colors indicate time of day (morning, afternoon, evening)
              </li>
              <li>Change duration to see how it affects available slots</li>
              <li>Booked appointments won't appear as available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotViewer;
